import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Injectable, OnDestroy, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';

import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { Novu } from '@novu/js';

import { NovuNotificationMapper } from '@shared/mapper/novu-notification-mapper';
import { NovuNotificationModel } from '@shared/model/novu-notification-model';
import { NOVU_IDENTITY_TOKEN, NovuIdentity } from '@core/token/novu-identity.token';

export type NovuInboxFilter = 'all' | 'unread' | 'archived';
export type NovuActionType = 'primary' | 'secondary';

const NOVU_IDENTITY_KEY = makeStateKey<NovuIdentity | null>('novu-identity');

// No usamos el Web Component de Novu (`@novu/js/ui`, pensado para React/SolidJS):
// este servicio consume el SDK headless (`novu.notifications.*`) y expone el estado
// necesario para que `NotificationInboxComponent` construya la UI con PrimeNG.
//
// La identidad de Novu (subscriberId + HMAC) se resuelve aquí mismo, sin depender
// de `AuthService`: se lee el valor ya calculado en `NOVU_IDENTITY_TOKEN`
// (server-only, ver `provideNovuIdentity`) y se transfiere al cliente por el mismo
// mecanismo de hidratación (`TransferState`) que ya usa el resto de la app.
@Injectable({
  providedIn: 'root',
})
export class NovuService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly transferState = inject(TransferState);
  private readonly novuIdentityToken = inject(NOVU_IDENTITY_TOKEN);

  private readonly notificationsSubject = new BehaviorSubject<NovuNotificationModel[]>([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly loadingMoreSubject = new BehaviorSubject<boolean>(false);
  private readonly hasMoreSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<boolean>(false);
  private readonly filterSubject = new BehaviorSubject<NovuInboxFilter>('unread');
  private readonly credentialsSubject = new BehaviorSubject<NovuIdentity | null>(null);
  // Cuenta la llamada a `loadInbox()` más reciente para descartar respuestas que
  // lleguen fuera de orden (p. ej. abrir/cerrar/reabrir el panel rápido dispara
  // varias peticiones con el mismo filtro; sin esto, la más lenta podía resolver
  // al final y pisar el listado ya actualizado por una petición posterior).
  private loadInboxRequestId = 0;

  public readonly notifications$: Observable<NovuNotificationModel[]> = this.notificationsSubject.asObservable();
  public readonly unreadCount$: Observable<number> = this.unreadCountSubject.asObservable();
  public readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  public readonly loadingMore$: Observable<boolean> = this.loadingMoreSubject.asObservable();
  public readonly hasMore$: Observable<boolean> = this.hasMoreSubject.asObservable();
  public readonly error$: Observable<boolean> = this.errorSubject.asObservable();
  public readonly filter$: Observable<NovuInboxFilter> = this.filterSubject.asObservable();

  private readonly credentialsSubscription: Subscription;
  private novu: Novu | null = null;
  private eventCleanups: Array<() => void> = [];

  constructor() {
    this.initializeIdentity();

    this.credentialsSubscription = this.credentialsSubject.subscribe(credentials => {
      if (isPlatformBrowser(this.platformId)) {
        this.handleCredentialsChange(credentials);
      }
    });
  }

  public ngOnDestroy(): void {
    this.credentialsSubscription.unsubscribe();
    this.clearNovuInstance();
  }

  private initializeIdentity(): void {
    if (isPlatformServer(this.platformId)) {
      this.transferState.set(NOVU_IDENTITY_KEY, this.novuIdentityToken);
      this.credentialsSubject.next(this.novuIdentityToken);
    } else if (isPlatformBrowser(this.platformId)) {
      const identity = this.transferState.get(NOVU_IDENTITY_KEY, null);

      this.credentialsSubject.next(identity);
      this.transferState.remove(NOVU_IDENTITY_KEY);
    }
  }

  // Carga la primera página de notificaciones (según el filtro activo) y el conteo
  // de no leídas. Se invoca al abrir el inbox, al cambiar de pestaña y tras
  // inicializar la sesión con nuevas credenciales.
  public async loadInbox(): Promise<void> {
    if (!this.novu) {
      return;
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(false);

    const requestId = ++this.loadInboxRequestId;
    const filter = this.filterSubject.value;
    const [notificationsResult, countResult] = await Promise.all([
      this.novu.notifications.list({
        archived: filter === 'archived',
        read: filter === 'unread' ? false : undefined,
        // La caché en memoria del SDK solo se sincroniza vía websocket
        // (`notifications.notification_received`); si ese evento llega tarde o
        // se pierde, el panel puede quedar desfasado del conteo (que sí siempre
        // pega al servidor). Forzamos datos frescos en cada apertura/cambio de tab.
        useCache: false,
      }),
      this.novu.notifications.count({ archived: false, read: false }),
    ]);

    // Si mientras esta petición estaba en vuelo se disparó otra `loadInbox()`
    // (cambio de pestaña, o abrir/cerrar/reabrir el panel rápido con el mismo
    // filtro), esta respuesta ya está obsoleta aunque el filtro no haya cambiado:
    // sin esta guarda, la más lenta podía resolver al final y pisar el listado
    // ya actualizado por la petición más reciente.
    if (requestId !== this.loadInboxRequestId) {
      return;
    }

    if (notificationsResult.error || countResult.error) {
      this.errorSubject.next(true);
      this.loadingSubject.next(false);
      return;
    }

    const notifications = notificationsResult.data?.notifications ?? [];

    this.notificationsSubject.next(notifications.map(notification => NovuNotificationMapper.mapToModel(notification)));
    this.unreadCountSubject.next(countResult.data?.count ?? 0);
    this.hasMoreSubject.next(notificationsResult.data?.hasMore ?? false);
    this.loadingSubject.next(false);

    void this.markAllAsSeen();
  }

  // Trae la siguiente página del listado activo, usando el id de la última
  // notificación cargada como cursor. Se invoca desde el botón "Cargar más".
  public async loadMore(): Promise<void> {
    if (!this.novu || !this.hasMoreSubject.value || this.loadingMoreSubject.value) {
      return;
    }

    const current = this.notificationsSubject.value;
    const lastId = current.at(-1)?.getId();

    if (!lastId) {
      return;
    }

    this.loadingMoreSubject.next(true);

    const filter = this.filterSubject.value;
    const { data, error } = await this.novu.notifications.list({
      archived: filter === 'archived',
      read: filter === 'unread' ? false : undefined,
      after: lastId,
      useCache: false,
    });

    if (error) {
      this.loadingMoreSubject.next(false);
      return;
    }

    this.notificationsSubject.next([
      ...current,
      ...(data?.notifications ?? []).map(notification => NovuNotificationMapper.mapToModel(notification)),
    ]);
    this.hasMoreSubject.next(data?.hasMore ?? false);
    this.loadingMoreSubject.next(false);
  }

  // Marca como "vistas" (distinto de "leídas") las notificaciones de la página
  // cargada, igual que hace el inbox oficial de Novu al abrir el panel.
  private async markAllAsSeen(): Promise<void> {
    if (!this.novu) {
      return;
    }

    const notificationIds = this.notificationsSubject.value
      .filter(notification => !notification.getIsSeen())
      .map(notification => notification.getId());

    if (notificationIds.length === 0) {
      return;
    }

    const { error } = await this.novu.notifications.seenAll({ notificationIds });

    if (error) {
      return;
    }

    this.notificationsSubject.next(
      this.notificationsSubject.value.map(notification =>
        notificationIds.includes(notification.getId()) ? notification.withIsSeen(true) : notification
      )
    );
  }

  // Cambia la pestaña activa (Todas / No leídas / Archivadas) y recarga el listado.
  public setFilter(filter: NovuInboxFilter): void {
    if (this.filterSubject.value === filter) {
      return;
    }

    this.filterSubject.next(filter);
    void this.loadInbox();
  }

  public async markAsRead(notificationId: string): Promise<void> {
    if (!this.novu) {
      return;
    }

    const { error } = await this.novu.notifications.read({ notificationId });

    if (error) {
      return;
    }

    // En la pestaña "No leídas" la notificación deja de pertenecer al filtro activo
    // en cuanto se marca como leída, así que se quita de la lista en vez de solo
    // actualizar su bandera (que la dejaría visible pero ya sin sentido ahí).
    this.notificationsSubject.next(
      this.filterSubject.value === 'unread'
        ? this.notificationsSubject.value.filter(notification => notification.getId() !== notificationId)
        : this.notificationsSubject.value.map(notification =>
            notification.getId() === notificationId ? notification.withIsRead(true) : notification
          )
    );
    this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
  }

  public async markAllAsRead(): Promise<void> {
    if (!this.novu) {
      return;
    }

    const { error } = await this.novu.notifications.readAll();

    if (error) {
      return;
    }

    this.notificationsSubject.next(
      this.filterSubject.value === 'unread'
        ? []
        : this.notificationsSubject.value.map(notification => notification.withIsRead(true))
    );
    this.unreadCountSubject.next(0);
  }

  public async archive(notificationId: string): Promise<void> {
    if (!this.novu) {
      return;
    }

    const target = this.notificationsSubject.value.find(notification => notification.getId() === notificationId);
    const { error } = await this.novu.notifications.archive({ notificationId });

    if (error) {
      return;
    }

    this.notificationsSubject.next(
      this.notificationsSubject.value.filter(notification => notification.getId() !== notificationId)
    );

    if (target && !target.getIsRead()) {
      this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
    }
  }

  public async unarchive(notificationId: string): Promise<void> {
    if (!this.novu) {
      return;
    }

    const target = this.notificationsSubject.value.find(notification => notification.getId() === notificationId);
    const { error } = await this.novu.notifications.unarchive({ notificationId });

    if (error) {
      return;
    }

    this.notificationsSubject.next(
      this.notificationsSubject.value.filter(notification => notification.getId() !== notificationId)
    );

    if (target && !target.getIsRead()) {
      this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
    }
  }

  public async deleteNotification(notificationId: string): Promise<void> {
    if (!this.novu) {
      return;
    }

    const target = this.notificationsSubject.value.find(notification => notification.getId() === notificationId);
    const { error } = await this.novu.notifications.delete({ notificationId });

    if (error) {
      return;
    }

    this.notificationsSubject.next(
      this.notificationsSubject.value.filter(notification => notification.getId() !== notificationId)
    );

    if (target && !target.getIsRead() && !target.getIsArchived()) {
      this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
    }
  }

  // Completa la acción primaria/secundaria definida por el workflow (ej. "Aceptar
  // invitación"), delegando a `completePrimary`/`completeSecondary` del SDK.
  public async completeAction(notificationId: string, actionType: NovuActionType): Promise<void> {
    if (!this.novu) {
      return;
    }

    const { error } =
      actionType === 'primary'
        ? await this.novu.notifications.completePrimary({ notificationId })
        : await this.novu.notifications.completeSecondary({ notificationId });

    if (error) {
      return;
    }

    this.notificationsSubject.next(
      this.notificationsSubject.value.map(notification => {
        if (notification.getId() !== notificationId) {
          return notification;
        }

        const current = actionType === 'primary' ? notification.getPrimaryAction() : notification.getSecondaryAction();

        if (!current) {
          return notification;
        }

        const completed = { ...current, isCompleted: true };

        return actionType === 'primary'
          ? notification.withPrimaryAction(completed)
          : notification.withSecondaryAction(completed);
      })
    );
  }

  private handleCredentialsChange(credentials: NovuIdentity | null): void {
    this.clearNovuInstance();

    if (!credentials) {
      return;
    }

    this.novu = new Novu({
      subscriber: credentials.subscriberId,
      subscriberHash: credentials.subscriberHash,
      applicationIdentifier: credentials.applicationIdentifier,
      apiUrl: credentials.apiUrl || undefined,
      socketUrl: credentials.socketUrl || undefined,
    });

    this.subscribeToRealtimeEvents(this.novu);
    void this.loadInbox();
  }

  // Mantiene el badge y la lista sincronizados sin recargar cuando llega una
  // notificación nueva o cambia el conteo de no leídas por websocket.
  private subscribeToRealtimeEvents(novu: Novu): void {
    this.eventCleanups.push(
      novu.on('notifications.notification_received', ({ result }) => {
        // Una notificación recién recibida nunca llega archivada: si la pestaña
        // activa es "Archivadas", insertarla ahí mezclaría datos de otro filtro.
        if (!result || this.filterSubject.value === 'archived') {
          return;
        }

        this.notificationsSubject.next([NovuNotificationMapper.mapToModel(result), ...this.notificationsSubject.value]);
      }),
      novu.on('notifications.unread_count_changed', ({ result }) => {
        if (!result) {
          return;
        }

        this.unreadCountSubject.next(result.total);
      })
    );
  }

  private clearNovuInstance(): void {
    this.eventCleanups.forEach(cleanup => cleanup());
    this.eventCleanups = [];
    this.novu = null;
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
    this.hasMoreSubject.next(false);
    this.loadingMoreSubject.next(false);
    this.errorSubject.next(false);
    this.loadingSubject.next(false);
    this.filterSubject.next('unread');
  }
}
